import React, { createContext, useState } from 'react';
import { produce } from 'immer';
import { get, has, set } from 'lodash';
import {
  type RestModalCard,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  type OfferCategory,
  type OfferContentType,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  getDefaultActionContent,
  getDefaultActionSteps,
} from '../content/action-content';
import { type Action, type Layout } from '.';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

export type Goal = {
  name: string;
  category: EnumLike<typeof OfferCategory>;
  type: EnumLike<typeof OfferContentType>;
  info: {
    title: string;
    details: string;
  };
  forwards: {
    text?: string;
  };
};
export const globalProduceUpdatedAction = (
  currentAction: RestModalForwards,
  newAction: Action,
  goal: Goal
) => {
  const newActionObj = getDefaultActionContent(newAction.forwards!.action);
  const updatedAction = {
    ...currentAction,
    ...newActionObj,
    ...goal.forwards,
  };
  return updatedAction;
};

export const globalProduceLayoutWithGoal = (
  layoutToEdit: Layout,
  goal: Partial<Goal>
): RestOffer => {
  // console.log(goal, layoutToEdit);
  const result = produce(layoutToEdit, (draft) => {
    const { offer } = draft;
    offer.category = goal.category!;
    offer.type = goal.type!;
    offer.display_name = goal.name || offer.display_name;
    const primaryCard = offer.cards.find(
      (c) => c.name === offer.primary_card_name
    )!;

    set(primaryCard, 'requires.info', {
      ...get(primaryCard, 'requires.info'),
      ...goal.info,
    });

    if (has(primaryCard, 'requires.forwards')) {
      set(primaryCard, 'requires.forwards', {
        ...get(primaryCard, 'requires.forwards')!,
        ...goal?.forwards,
      });
    } else if (has(primaryCard, 'requires.generic_actions')) {
      if (layoutToEdit.group === 'finalOffer') {
        set(primaryCard, 'requires.generic_actions[0]', {
          ...get(primaryCard, 'requires.generic_actions[0]'),
          ...goal?.forwards,
        });
      } else {
        const genericActions = get(
          primaryCard,
          'requires.generic_actions'
        ) as RestModalForwards[];
        set(
          primaryCard,
          'requires.generic_actions',
          genericActions.map((ga) => ({
            ...ga,
            ...goal?.forwards,
          }))
        );
      }
    }

    offer.cards = [primaryCard];
  });

  // console.log(result);
  return result.offer;
};

export const globalFinalizeOffer = (newLayout: Layout): Layout => {
  const result = produce(newLayout, (draft) => {
    const { offer } = draft;

    const primaryCard = offer.cards.find(
      (c) => c.name === offer.primary_card_name
    )!;

    let actionItems: RestModalForwards[] = [];
    const cards: RestModalCard[] = [];
    // forwards
    if (has(primaryCard, 'requires.forwards')) {
      actionItems.push(
        get(primaryCard, 'requires.forwards') as RestModalForwards
      );
    }
    // generic actions
    else if (has(primaryCard, 'requires.generic_actions')) {
      const genericActions = get(
        primaryCard,
        'requires.generic_actions',
        []
      ) as RestModalForwards[];
      actionItems.push(...genericActions);
    }

    actionItems = actionItems.map((ai) => {
      // Generate values
      const additionalActionCards = getDefaultActionSteps(offer, ai.action);
      const actionContent = getDefaultActionContent(ai.action)!;
      if (additionalActionCards.length) {
        actionContent.modal = additionalActionCards[0]!.name;
      }
      actionContent.text = ai.text;

      cards.push(...additionalActionCards);
      return {
        ...ai,
        ...actionContent,
      };
    });

    // forwards
    if (has(primaryCard, 'requires.forwards')) {
      set(primaryCard, 'requires.forwards', get(actionItems, '0'));
    }
    // generic actions
    else if (has(primaryCard, 'requires.generic_actions')) {
      set(primaryCard, 'requires.generic_actions', actionItems);
    }

    offer.cards = [primaryCard, ...cards];
  });

  return result;
};

export const NewOfferFlowContext = createContext<{
  // Goal (step 1)
  goal?: Goal;
  setGoal: React.Dispatch<React.SetStateAction<Goal | undefined>>;

  // Layout
  layout?: Layout;
  setLayout: React.Dispatch<React.SetStateAction<Layout | undefined>>;

  produceLayoutWithGoal: (layoutToEdit: Layout) => RestOffer;
  produceUpdatedAction: (
    currentAction: RestModalForwards,
    newAction: Action
  ) => RestModalForwards;

  // generates additional modals for offers with certain actions
  finalizeOffer: (layout: Layout) => Layout;
}>(undefined!);

export const NewOfferFlowContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [goal, setGoal] = useState<Goal>();
  const [layout, setLayout] = useState<Layout>();

  return (
    <NewOfferFlowContext.Provider
      value={{
        // Goal
        goal,
        setGoal,

        // Layout
        layout,
        setLayout,

        produceLayoutWithGoal: (layoutToEdit: Layout) =>
          globalProduceLayoutWithGoal(layoutToEdit, goal!),
        produceUpdatedAction: (
          currentAction: RestModalForwards,
          newAction: Action
        ) => globalProduceUpdatedAction(currentAction, newAction, goal!),

        // generates additional modals for offers with certain actions
        finalizeOffer: globalFinalizeOffer,
      }}
    >
      {children}
    </NewOfferFlowContext.Provider>
  );
};
